import React, { useEffect, useRef, useState } from 'react';
import api from '../services/authService';
import '../styles/navigator.css';

/**
 * The dashboard assistant — a plain chat, ported from the cover chat in
 * D:\cowork_project\information\src\app-shell.html and restyled for the light
 * dashboard theme.
 *
 * Differences from the reference, all on purpose:
 *   - the Gemini key is NOT held in this browser. It lives on the server; this
 *     component only ever sees a masked hint, and only when the signed-in user
 *     is a superadmin — who is the only one shown the "API key" panel;
 *   - messages go to POST /api/assistant/chat, which holds the key and calls
 *     Gemini. Any user can chat once a superadmin has set the key;
 *   - the "why it failed" wording comes from the server, which knows the
 *     upstream status; the component just shows it.
 */

const IconSend = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4 20-7z" />
  </svg>
);

// Remembered per browser so the choice survives a reload; the mode itself is
// applied server-side per request, so nothing here is authoritative.
const MODE_STORE = 'biomedAgentMode';
const loadMode = () => {
  try { return localStorage.getItem(MODE_STORE) === 'short' ? 'short' : 'normal'; }
  catch (e) { return 'normal'; }
};

const NavigatorChat = ({ user }) => {
  const isSuper = user?.role === 'superadmin';
  const [turns, setTurns] = useState([]);            // { role: 'user' | 'model', text, err? }
  const [mode, setMode] = useState(loadMode);        // 'normal' | 'short'
  const [input, setInput] = useState('');
  const [awaiting, setAwaiting] = useState(false);
  const [slow, setSlow] = useState(false);
  const [status, setStatus] = useState(null);        // { configured, model, keyHint? } | null (loading) | { error }
  const [keyOpen, setKeyOpen] = useState(false);
  const [keyDraft, setKeyDraft] = useState('');
  const [keyBusy, setKeyBusy] = useState(false);
  const [keyMsg, setKeyMsg] = useState('');
  const threadRef = useRef(null);
  const inputRef = useRef(null);

  const loadStatus = async () => {
    try {
      const res = await api.get('/api/assistant/status');
      setStatus(res.data);
    } catch (e) {
      setStatus({ configured: false, error: true });
    }
  };
  useEffect(() => { loadStatus(); }, []);

  // Keep the newest message in view.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns, awaiting]);

  const autoGrow = (el) => {
    el.style.height = 'auto';
    el.style.height = Math.min(190, el.scrollHeight) + 'px';
  };

  const ask = async (text) => {
    const next = [...turns, { role: 'user', text }];
    setTurns(next);
    setAwaiting(true);
    setSlow(false);
    // Dots alone stop reassuring after about ten seconds; past that the reader
    // needs telling that the wait is the model, not a hang.
    const slowTimer = setTimeout(() => setSlow(true), 9000);
    try {
      const res = await api.post('/api/assistant/chat', {
        turns: next.filter((t) => !t.err).map((t) => ({ role: t.role, text: t.text })),
        mode,
      });
      const d = res.data || {};
      if (d.text) {
        setTurns((cur) => [...cur, { role: 'model', text: d.text }]);
      } else {
        setTurns((cur) => [...cur, {
          role: 'model', err: true,
          text: d.reason === 'SAFETY'
            ? 'The answer was withheld by Gemini\u2019s safety filter. Try rephrasing the question.'
            : 'No answer came back. Try again.',
        }]);
      }
    } catch (e) {
      const d = e.response?.data || {};
      let msg = d.message || 'Could not reach the AI agent.';
      if (d.detail) msg += '\n\n' + d.detail;
      if (d.code === 'NOT_CONFIGURED') loadStatus();
      setTurns((cur) => [...cur, { role: 'model', err: true, text: msg }]);
    } finally {
      clearTimeout(slowTimer);
      setAwaiting(false);
      setSlow(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || awaiting) return;
    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    ask(text);
  };

  // Enter sends, Shift+Enter breaks the line — the convention every chat uses.
  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.currentTarget.form.requestSubmit();
    }
  };

  const saveKey = async (clear = false) => {
    const v = clear ? '' : keyDraft.trim();
    if (!clear && !v) return;
    setKeyBusy(true);
    setKeyMsg('');
    try {
      const res = await api.put('/api/assistant/key', { key: v });
      setStatus(res.data);
      setKeyDraft('');
      setKeyMsg(clear ? 'Key removed.' : 'Key saved.');
      if (!clear) setKeyOpen(false);
    } catch (e) {
      setKeyMsg(e.response?.data?.message || 'Could not save the key.');
    } finally {
      setKeyBusy(false);
    }
  };

  const configured = !!status?.configured;

  const pickMode = (m) => {
    setMode(m);
    try { localStorage.setItem(MODE_STORE, m); } catch (e) { /* private mode — fine */ }
  };

  return (
    <section className="navigator" aria-label="BioMed AI Agent">
      <header className="nav-head">
        <div>
          <h2 className="nav-title">BioMed AI Agent</h2>
          <p className="nav-blurb">Ask anything. Answers follow the language you write in.</p>
        </div>
        <span className={`nav-dot ${configured ? 'is-on' : ''}`} title={configured ? `Ready · ${status.model}` : 'Not set up'} aria-hidden="true" />
      </header>

      <div className="nav-thread" ref={threadRef} role="log" aria-live="polite" aria-label="Conversation">
        {turns.length === 0 && !awaiting && (
          <p className="nav-empty">
            {status === null
              ? 'Connecting…'
              : configured
                ? 'Start whenever you like — for example, “draft a follow-up email to a clinic whose results are back” or “explain what dysbiosis means in plain words”.'
                : isSuper
                  ? 'The AI agent is not set up yet. Add the Gemini API key below — once saved it is kept on the server for everyone, on every browser.'
                  : 'The AI agent is not set up yet. Ask a super admin to add the API key.'}
          </p>
        )}

        {turns.map((t, i) => (
          <div key={i} className={`nav-msg ${t.role === 'user' ? 'me' : 'ai'}${t.err ? ' err' : ''}`}>
            <span className="who">{t.role === 'user' ? 'You' : 'AI Agent'}</span>
            <div className="body">{t.text}</div>
          </div>
        ))}

        {awaiting && (
          <div className="nav-msg ai pending">
            <span className="who">AI Agent</span>
            <div className="body">
              <span className="dots" aria-label="Thinking"><i /><i /><i /></span>
              {slow && <span className="slow"> Still thinking, one moment…</span>}
            </div>
          </div>
        )}
      </div>

      {/* Answer length. "Normal" is the usual reply; "Short answer" caps the
          model at 1–2 sentences (enforced server-side in the system prompt).
          It applies from the next message on, so mid-conversation switching
          works. */}
      <div className="nav-mode" role="radiogroup" aria-label="Answer length">
        <button
          type="button"
          className={`nav-mode-opt${mode === 'normal' ? ' is-active' : ''}`}
          role="radio"
          aria-checked={mode === 'normal'}
          onClick={() => pickMode('normal')}
        >
          Normal
        </button>
        <button
          type="button"
          className={`nav-mode-opt${mode === 'short' ? ' is-active' : ''}`}
          role="radio"
          aria-checked={mode === 'short'}
          onClick={() => pickMode('short')}
        >
          Short answer
        </button>
        <span className="nav-mode-hint">{mode === 'short' ? 'Replies end in 1–2 sentences.' : ''}</span>
      </div>

      <form className="nav-composer" onSubmit={onSubmit}>
        <label htmlFor="nav-input" className="sr-only">Message</label>
        <textarea
          id="nav-input"
          ref={inputRef}
          rows={1}
          value={input}
          placeholder={configured ? 'Type a question and press Enter to send' : 'AI agent not set up yet'}
          autoComplete="off"
          disabled={!configured || awaiting}
          onChange={(e) => { setInput(e.target.value); autoGrow(e.target); }}
          onKeyDown={onKeyDown}
        />
        <button type="submit" className="nav-send" aria-label="Send" disabled={!configured || awaiting || !input.trim()}>
          <IconSend />
        </button>
      </form>

      <div className="nav-foot">
        <button type="button" className="nav-link" onClick={() => setTurns([])} disabled={!turns.length}>Clear conversation</button>
        {isSuper && (
          <button type="button" className="nav-link" onClick={() => { setKeyOpen((o) => !o); setKeyMsg(''); }}>
            {keyOpen ? 'Close' : 'API key'}
          </button>
        )}
      </div>

      {isSuper && keyOpen && (
        <div className="nav-key">
          <p className="nk-h">Gemini API key</p>
          <p className="nk-s">
            {configured
              ? <>Saved ({status.keyHint}){status.updatedBy ? <> by <strong>{status.updatedBy}</strong></> : null}{status.updatedAt ? <> on {new Date(status.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</> : null}. It is kept encrypted in the database, so every user on every browser can use the agent — nobody needs to enter it again. Paste a new key and save to replace it.</>
              : <>Not set. Paste the key from Google AI Studio. It is kept encrypted in the database and never sent to anyone&apos;s browser — enter it once and it stays set for everyone.</>}
          </p>
          <div className="nk-row">
            <input
              type="password"
              value={keyDraft}
              onChange={(e) => setKeyDraft(e.target.value)}
              placeholder="AIza…"
              autoComplete="off"
              spellCheck="false"
              disabled={keyBusy}
            />
            <button type="button" className="nk-save" onClick={() => saveKey(false)} disabled={keyBusy || !keyDraft.trim()}>Save</button>
            {configured && (
              <button type="button" className="nk-clear" onClick={() => saveKey(true)} disabled={keyBusy}>Remove</button>
            )}
          </div>
          {keyMsg && <p className="nk-s nk-msg">{keyMsg}</p>}
        </div>
      )}
    </section>
  );
};

export default NavigatorChat;
